import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { clientPath } from '../lib/clientLink.js';
import { Toggle, fmtDateTime } from '../components/FormBits.jsx';
import { SaveStatus, useSaveStatus } from '../components/SaveStatus.jsx';
import { SeoChecklistView, SeoPreviewView, CATEGORY_LABEL, OWNER_LABEL } from './SeoRecommendations.jsx';
import { seoRecsMode } from '../modules/index.js';
import { seoChecklistText, copyText } from '../lib/seoCopyText.js';

// Session 22: agency SEO checklist for one client, the "SEO checklist" tab of
// /admin/clients/:clientId/settings (?tab=seo). The Inbox route
// /admin/clients/:clientId/seo-tasks redirects there.
//   Draft    made by the droplet from the baseline analysis (every run, or
//            tools/seo_tasks_backfill.py). "Preview as client", an editable
//            task list (title, what to do, owner, priority, Hide) and
//            "Release to client" behind a confirm step. Releasing replaces
//            the client's current checklist: done items stay done, items no
//            longer found show as resolved.
//   Released progress, the agency can tick any task, the editor stays.
// Session 23: "Who makes the fixes" (pack.seo_fixes_by, sets every task's
// owner after a confirm), the release and preview follow the client's SEO
// recommendations switch (Preview = the locked teaser, no email), and
// "Copy as text" for pasting the visible list into an email.
// Session 24: buckets with the caps (deferred rows shown read-only), the fix
// channel dropdown, "Prepare fix" on fixable items, and the Fixes filter
// (one panel per open fix job: proposal, diff, Approve / Reject, status).

const MODE_LABEL = { on: 'On', preview: 'Preview (locked)', off: 'Off' };
const FIXES_BY_LABEL = { client: 'Client (self-serve)', agency: 'Digital Lotus' };

export default function AdminSeoTasks() {
  const { clientId } = useParams();
  const [params] = useSearchParams();
  // Session 24: the Inbox's SEO fix rows open the tab on the Fixes filter.
  const fixes = params.get('fixes') ? '&fixes=1' : '';
  return <Navigate to={clientPath(`/admin/clients/${clientId}/settings?tab=seo${fixes}`, clientId)} replace />;
}

const FIX_CHANNEL_LABEL = {
  none: 'None (no fixes offered)', dl_repo: 'Digital Lotus website repo (pull request)',
  lovable: 'Lovable (through Cowork)', dave_api: 'Build A Better Website (not offered yet)',
};
const FIX_STATUS_LABEL = {
  requested: 'Requested by the client', preparing: 'Preparing', proposed: 'Ready for your approval',
  approved: 'Approved', pr_open: 'Pull request open', applied: 'Applied', rejected: 'Rejected', failed: 'Failed',
  waiting_for_cowork: 'Waiting for Cowork',
};
const OPEN_FIX = new Set(['requested', 'preparing', 'proposed', 'approved', 'pr_open', 'waiting_for_cowork', 'failed']);

export function SeoTasksTab({ clientId, clientName, pack, onPackChange }) {
  const mode = seoRecsMode(pack?.entitlements);
  const channel = pack?.seo_fix_channel || 'none';
  const [params, setParams] = useSearchParams();
  const showFixes = params.get('fixes') === '1';
  const [lists, setLists] = useState(undefined);       // undefined = loading
  const [tasks, setTasks] = useState({});              // list_id -> rows
  const [buckets, setBuckets] = useState({});          // list_id -> v_portal_seo_buckets rows
  const [caps, setCaps] = useState({});                // issue_type -> capability on this client's channel
  const [jobs, setJobs] = useState([]);                // v_portal_seo_fix_jobs for this client
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('v_portal_seo_task_list').select('*').eq('client_id', clientId);
    if (e) { setError(e.message); return; }
    const rows = data || [];
    const byList = {};
    const bByList = {};
    for (const l of rows) {
      const [{ data: t, error: tErr }, { data: b, error: bErr }] = await Promise.all([
        supabase.from('v_portal_seo_tasks').select('*').eq('list_id', l.list_id).order('sort'),
        supabase.from('v_portal_seo_buckets').select('*').eq('list_id', l.list_id),
      ]);
      if (tErr) { setError(tErr.message); return; }
      byList[l.list_id] = t || [];
      bByList[l.list_id] = bErr ? null : (b || []);   // null: counts from the tasks
    }
    const [{ data: c }, { data: j }] = await Promise.all([
      supabase.from('v_portal_seo_fix_capabilities').select('issue_type, capability').eq('channel', channel),
      supabase.from('v_portal_seo_fix_jobs').select('*').eq('client_id', clientId).order('updated_at', { ascending: false }),
    ]);
    setCaps(Object.fromEntries((c || []).map((r) => [r.issue_type, r.capability])));
    setJobs(j || []);
    setLists(rows); setTasks(byList); setBuckets(bByList); setError(null);
  }, [clientId, channel]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="empty"><h2>Couldn't load the SEO checklist</h2><p>{error}</p></div>;
  if (lists === undefined) return <p className="lede">Loading…</p>;

  const draft = lists.find((l) => l.status === 'draft');
  const released = lists.find((l) => l.status === 'released');

  async function onToggle(listId, task, done) {
    const stamp = done ? new Date().toISOString() : null;
    setTasks((s) => ({ ...s, [listId]: s[listId].map((t) => (t.task_id === task.task_id ? { ...t, done_at: stamp } : t)) }));
    const { error: e } = await supabase.rpc('portal_seo_toggle_task', { p_task_id: task.task_id, p_done: done });
    if (e) {
      setTasks((s) => ({ ...s, [listId]: s[listId].map((t) => (t.task_id === task.task_id ? { ...t, done_at: task.done_at } : t)) }));
      setBanner({ tone: 'err', text: `Couldn't save that tick: ${e.message}` });
    } else {
      const { data: b } = await supabase.from('v_portal_seo_buckets').select('*').eq('list_id', listId);
      if (b) setBuckets((s) => ({ ...s, [listId]: b }));
    }
  }

  const openJobs = jobs.filter((j) => OPEN_FIX.has(j.status));
  const jobById = Object.fromEntries(jobs.map((j) => [j.job_id, j]));
  const setFixes = (on) => {
    const p = new URLSearchParams(params);
    if (on) p.set('fixes', '1'); else p.delete('fixes');
    setParams(p);
  };

  async function prepareFix(task) {
    setBanner(null);
    const { error: e } = await supabase.rpc('portal_seo_prepare_fix', { p_task_id: task.task_id });
    if (e) { setBanner({ tone: 'err', text: `Couldn't prepare the fix: ${e.message}` }); return; }
    setBanner({ tone: 'ok', text: 'Preparing the fix. It shows under Fixes when it is ready for your approval.' });
    await load();
  }

  // Under each task in the agency checklist: "Prepare fix" or the fix status.
  const renderTaskExtra = (t, readOnly) => {
    if (readOnly) return null;
    const cap = caps[t.issue_type] || 'no';
    const job = t.fix_job_id ? jobById[t.fix_job_id] : null;
    if (job && job.status !== 'rejected') {
      return (
        <p className="seo-fix-line">
          <span className={`gmb-chip seo-fix-status fix-${job.status}`}>{FIX_STATUS_LABEL[job.status] || job.status}</span>
          {' '}<button type="button" className="link-btn" onClick={() => setFixes(true)}>Open in Fixes</button>
          {job.status === 'pr_open' && job.pr_url && <>{' · '}<a href={job.pr_url} target="_blank" rel="noopener noreferrer">Pull request</a></>}
          {['requested', 'failed'].includes(job.status) && (cap === 'yes' || cap === 'review') && !isDoneRow(t) && (
            <>{' · '}<button type="button" className="link-btn" onClick={() => prepareFix(t)}>Prepare fix</button></>
          )}
        </p>
      );
    }
    if ((cap === 'yes' || cap === 'review') && !isDoneRow(t)) {
      return (
        <p className="seo-fix-line">
          <button type="button" className="btn btn-small" onClick={() => prepareFix(t)}>Prepare fix</button>
          <span className="hint" style={{ margin: '0 0 0 8px' }}>
            {cap === 'yes' ? 'Claude prepares the exact change.' : 'Claude drafts it; you edit it before approving.'}
          </span>
        </p>
      );
    }
    return null;
  };

  return (
    <>
      <p className="hint" style={{ marginTop: 0, marginBottom: 14 }}>
        The on-page findings of {clientName}'s baseline analysis as a checklist. Each task points at a page and what the analysis found there.
        Check the wording and owners, hide anything that doesn't apply, then release.
        {' '}SEO recommendations is <strong>{MODE_LABEL[mode]}</strong> for {clientName} (change it under Edit client).
      </p>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}

      <FixesByControl clientId={clientId} initial={pack?.seo_fixes_by === 'agency' ? 'agency' : 'client'}
                      count={[draft, released].filter(Boolean).reduce((n, l) => n + (tasks[l.list_id] || []).length, 0)}
                      onChange={load} />
      <FixChannelControl clientId={clientId} initial={channel} onChange={async () => { if (onPackChange) await onPackChange(); await load(); }} />

      <div className="gmb-filters seo-fix-filter" role="tablist" aria-label="Show">
        <button type="button" role="tab" aria-selected={!showFixes} className={`gmb-filter ${!showFixes ? 'active' : ''}`}
                onClick={() => setFixes(false)}>Checklist</button>
        <button type="button" role="tab" aria-selected={showFixes} className={`gmb-filter ${showFixes ? 'active' : ''}`}
                onClick={() => setFixes(true)}>Fixes<span className="gmb-filter-count">{openJobs.length}</span></button>
      </div>

      {showFixes && (
        <section className="review-section">
          <h2>Fixes</h2>
          <p className="hint" style={{ marginTop: 0 }}>
            Open fix jobs for {clientName}. Fix channel: <strong>{FIX_CHANNEL_LABEL[channel] || channel}</strong>.
            {' '}Approving a website-repo fix opens a pull request on GitHub (you merge it there); a Lovable fix waits for Cowork.
          </p>
          {openJobs.length === 0 ? <p className="results-empty">No open fixes.</p>
            : openJobs.map((j) => <FixPanel key={j.job_id} job={j} onChange={load} onPrepare={async (taskId) => {
                const { error: e } = await supabase.rpc('portal_seo_prepare_fix', { p_task_id: taskId });
                if (e) return e.message;
                await load();
                return null;
              }} />)}
          {jobs.length > openJobs.length && (
            <p className="hint">{jobs.length - openJobs.length} closed fix{jobs.length - openJobs.length === 1 ? '' : 'es'} (applied or rejected) not shown.</p>
          )}
        </section>
      )}

      {!draft && !released && (
        <div className="empty">
          <h2>No checklist yet</h2>
          <p>A draft appears here after the next baseline analysis, or once the backfill has run on the droplet
            (<code>tools/seo_tasks_backfill.py --dry-run</code>, then <code>--apply</code>).</p>
        </div>
      )}

      {!showFixes && draft && (
        <DraftPanel list={draft} tasks={tasks[draft.list_id] || []} buckets={buckets[draft.list_id] ?? null} clientName={clientName}
                    replacing={released} onChange={load} mode={mode} clientId={clientId} renderTaskExtra={renderTaskExtra} />
      )}

      {!showFixes && released && (
        <>
          <ReleasedSummary list={released} tasks={(tasks[released.list_id] || []).filter((t) => !t.deferred)} />
          <section className="review-section">
            <div className="section-heading" style={{ margin: '0 0 6px' }}>
              <h2>{mode === 'on' ? 'What the client sees' : 'The full checklist'}</h2>
              <span className="count">You can tick any task here.</span>
              <CopyAsText clientName={clientName} list={released} tasks={tasks[released.list_id] || []} />
            </div>
            {mode === 'preview' && <LockedPreview clientId={clientId} clientName={clientName} />}
            {mode === 'off' && <p className="hint">SEO recommendations is Off, so {clientName} doesn't see this list.</p>}
            <div className="gmb-preview-frame">
              <SeoChecklistView list={released} tasks={(tasks[released.list_id] || []).filter((t) => !t.hidden && !t.deferred)}
                                buckets={buckets[released.list_id] ?? null} you={false}
                                deferredTasks={(tasks[released.list_id] || []).filter((t) => !t.hidden && t.deferred)}
                                canTick={(t) => !!t.can_tick} onToggle={(t, d) => onToggle(released.list_id, t, d)}
                                renderTaskExtra={renderTaskExtra} />
            </div>
          </section>
          <TaskEditor tasks={tasks[released.list_id] || []} onSaved={load} collapsed />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

// Session 23 ----------------------------------------------------------------

function FixesByControl({ clientId, initial, count, onChange }) {
  const [value, setValue] = useState(initial);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const saved = useSaveStatus();
  useEffect(() => { setValue(initial); }, [initial]);

  async function apply() {
    setBusy(true); saved.start();
    const { error } = await supabase.rpc('portal_seo_set_fixes_by', { p_client_id: clientId, p_value: pending });
    setBusy(false);
    if (error) { saved.fail(error.message); setPending(null); return; }
    saved.ok();
    setValue(pending); setPending(null);
    await onChange();
  }

  return (
    <>
      <div className="seo-fixes-by">
        <label className="field" style={{ margin: 0 }}>
          <span className="field-label">Who makes the fixes</span>
          <select value={value} onChange={(e) => e.target.value !== value && setPending(e.target.value)} disabled={busy}>
            <option value="client">{FIXES_BY_LABEL.client}</option>
            <option value="agency">{FIXES_BY_LABEL.agency}</option>
          </select>
        </label>
        <span className="hint" style={{ margin: 0 }}>Sets the owner of every item. You can still change single items below.</span>
        <SaveStatus status={saved.status} />
      </div>
      {pending && (
        <div className="modal-backdrop" onClick={() => !busy && setPending(null)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="seo-fixes-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="seo-fixes-title">Change who makes the fixes?</h2>
            <p>This sets all {count} item{count === 1 ? '' : 's'} to {pending === 'agency' ? 'Digital Lotus' : 'Client'}.</p>
            <div className="review-actions">
              <button type="button" className="btn" onClick={() => setPending(null)} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={apply} disabled={busy}>{busy ? 'Saving…' : 'Yes, set them all'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Session 24: the client's fix channel (pack.seo_fix_channel), agency only.
function FixChannelControl({ clientId, initial, onChange }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const saved = useSaveStatus();
  useEffect(() => { setValue(initial); }, [initial]);
  async function save(v) {
    setBusy(true); saved.start();
    const { error } = await supabase.rpc('portal_seo_set_fix_channel', { p_client_id: clientId, p_channel: v });
    setBusy(false);
    if (error) { saved.fail(error.message); return; }
    setValue(v); saved.ok();
    await onChange();
  }
  return (
    <div className="seo-fixes-by">
      <label className="field" style={{ margin: 0 }}>
        <span className="field-label">How Digital Lotus applies fixes</span>
        <select value={value} onChange={(e) => e.target.value !== value && save(e.target.value)} disabled={busy}>
          {Object.entries(FIX_CHANNEL_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <span className="hint" style={{ margin: 0 }}>Agency only. Decides which items show "Digital Lotus can fix this" (with the switch On).</span>
      <SaveStatus status={saved.status} />
    </div>
  );
}

// Session 24: one fix job: the proposal (editable for a `review` fix while
// it waits), the diff, Approve / Reject, then the status, the PR link or
// "Waiting for Cowork".
function FixPanel({ job, onChange, onPrepare }) {
  const proposal = job.proposal || {};
  const editable = job.status === 'proposed' && job.capability === 'review';
  const [text, setText] = useState(() => JSON.stringify(proposal, null, 2));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const saved = useSaveStatus();
  useEffect(() => { setText(JSON.stringify(job.proposal || {}, null, 2)); }, [job.job_id, job.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const taskId = Array.isArray(job.task_ids) ? job.task_ids[0] : null;

  async function rpc(name, args) {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc(name, args);
    setBusy(false);
    if (error) { setErr(error.message); return false; }
    await onChange();
    return true;
  }
  async function saveProposal() {
    let parsed;
    try { parsed = JSON.parse(text); } catch (e) { saved.fail(`the proposal isn't valid JSON (${e.message})`); return; }
    setBusy(true); saved.start();
    const { error } = await supabase.rpc('portal_seo_edit_fix_proposal', { p_job_id: job.job_id, p_proposal: parsed });
    setBusy(false);
    if (error) { saved.fail(error.message); return; }
    saved.ok();
    await onChange();
  }
  async function again() {
    setBusy(true); setErr(null);
    const e = await onPrepare(taskId);
    setBusy(false);
    if (e) setErr(e);
  }

  return (
    <div className={`seo-fix-panel fix-${job.status}`}>
      <div className="seo-fix-panel-head">
        <strong>{job.title || job.job_id}</strong>
        <span className={`gmb-chip seo-fix-status fix-${job.status}`}>{FIX_STATUS_LABEL[job.status] || job.status}</span>
        <span className="muted">{job.capability === 'yes' ? 'Exact change' : 'Draft for you to edit'} · {job.requested_by === 'client' ? 'asked by the client' : 'started by you'}</span>
      </div>
      {job.status === 'requested' && (
        <p className="hint">The client asked Digital Lotus to fix this. <button type="button" className="btn btn-small" disabled={busy || !taskId} onClick={again}>Prepare fix</button></p>
      )}
      {job.status === 'preparing' && <p className="hint">Claude is preparing the change. This usually takes a minute or two.</p>}
      {proposal.summary && <p>{proposal.summary}</p>}
      {proposal.kind === 'lovable' && proposal.instructions && !editable && (
        <><h4>What Cowork sends to Lovable</h4><p className="seo-fix-instructions">{proposal.instructions}</p></>
      )}
      {proposal.kind === 'onpage_seo' && Array.isArray(proposal.changes) && proposal.changes.length > 0 && !editable && (
        <ul className="seo-fix-changes">
          {proposal.changes.slice(0, 50).map((c, i) => (
            <li key={i}><code>{c.path}</code> {c.field}: {c.from ? <><s>{c.from}</s> → </> : null}<strong>{c.to}</strong></li>
          ))}
          {proposal.changes.length > 50 && <li className="muted">and {proposal.changes.length - 50} more (see the diff)</li>}
        </ul>
      )}
      {editable && (
        <label className="field">
          <span className="field-label">Proposal (edit before approving)</span>
          <textarea rows={8} className="mono" value={text} onChange={(e) => setText(e.target.value)} disabled={busy} />
          <span className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 6 }}>
            <button type="button" className="btn" onClick={saveProposal} disabled={busy}>Save proposal</button>
            <SaveStatus status={saved.status} />
          </span>
        </label>
      )}
      {job.diff && (
        <details className="seo-fix-diff">
          <summary>Diff{job.channel === 'dl_repo' && editable ? ' (rebuilt from your edited proposal when you approve)' : ''}</summary>
          <pre>{job.diff}</pre>
        </details>
      )}
      {job.status === 'proposed' && (
        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="btn btn-primary" disabled={busy}
                  onClick={() => rpc('portal_seo_decide_fix', { p_job_id: job.job_id, p_decision: 'approve' })}>Approve</button>
          <button type="button" className="btn" disabled={busy}
                  onClick={() => rpc('portal_seo_decide_fix', { p_job_id: job.job_id, p_decision: 'reject' })}>Reject</button>
        </div>
      )}
      {job.status === 'approved' && <p className="hint">{job.channel === 'dl_repo' ? 'Opening the pull request…' : 'Approved.'}</p>}
      {job.status === 'pr_open' && (
        <p>Pull request open: <a href={job.pr_url} target="_blank" rel="noopener noreferrer">{job.pr_url}</a>. Merge it on GitHub; the item is ticked off once it's merged.</p>
      )}
      {job.status === 'waiting_for_cowork' && (
        <p className="hint">Waiting for Cowork: it sends the change through Lovable, you review the diff there, then Cowork deploys and marks it applied.</p>
      )}
      {job.status === 'failed' && (
        <p className="banner banner-err">{job.error || 'The fix failed.'}{' '}
          <button type="button" className="link-btn" disabled={busy || !taskId} onClick={again}>Prepare again</button></p>
      )}
      {err && <div className="banner banner-err">{err}</div>}
    </div>
  );
}

const isDoneRow = (t) => !!(t.done_at || t.resolved_by_rerun_at);

function CopyAsText({ clientName, list, tasks }) {
  const [done, setDone] = useState(null);
  async function copy() {
    // what the client sees: hidden and deferred items left out
    const ok = await copyText(seoChecklistText({ clientName, baselineDate: list.baseline_date, tasks: tasks.filter((t) => !t.deferred) }));
    setDone(ok ? 'Copied' : 'Copy failed');
    setTimeout(() => setDone(null), 2500);
  }
  return (
    <span style={{ marginLeft: 'auto' }}>
      <button type="button" className="btn" onClick={copy}>Copy as text</button>
      {done && <span className="slot-filled" style={{ marginLeft: 8 }}>{done}</span>}
    </span>
  );
}

function LockedPreview({ clientId, clientName }) {
  const [data, setData] = useState(undefined);
  useEffect(() => {
    let live = true;
    supabase.rpc('portal_seo_preview', { p_client_id: clientId }).then(({ data: d }) => { if (live) setData(d || null); });
    return () => { live = false; };
  }, [clientId]);
  if (data === undefined) return <p className="lede">Loading…</p>;
  if (!data) return null;
  return (
    <div className="gmb-preview-frame" style={{ marginBottom: 14 }}>
      <div className="gmb-preview-ribbon">What {clientName} sees (Preview, locked)</div>
      <SeoPreviewView data={data} />
    </div>
  );
}

function DraftPanel({ list, tasks, buckets, clientName, replacing, onChange, mode, clientId, renderTaskExtra }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Session 24: shown = not hidden and not deferred (the caps); deferred rows
  // stay visible to you, read-only, under their bucket.
  const visible = tasks.filter((t) => !t.hidden && !t.deferred);
  const deferred = tasks.filter((t) => !t.hidden && t.deferred);
  const hiddenCount = tasks.filter((t) => t.hidden).length;
  const fromReport = tasks.some((t) => t.source === 'report');

  async function release() {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('portal_seo_release', { p_list_id: list.list_id });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setConfirm(false);
    await onChange();
  }

  return (
    <>
      <div className="banner banner-gate">
        <strong>Draft ready for your review.</strong> {clientName} can't see it until you press Release to client.
        {' '}{visible.length} task{visible.length === 1 ? '' : 's'} will be shown{hiddenCount ? `, ${hiddenCount} hidden` : ''}
        {deferred.length ? `, ${deferred.length} deferred (over the caps of 20 per bucket and 100 in all; they come on at later reruns)` : ''}.
        {' '}From the analysis {list.baseline_job_id}{list.baseline_date ? ` (${list.baseline_date})` : ''}.
      </div>
      {fromReport && (
        <div className="banner banner-waiting">
          <strong>Extracted from the written report.</strong> This baseline has no saved per-page data, so these tasks were read from lines of the
          report's Site audit section ("What we found" quotes each line). Check them more carefully than usual.
        </div>
      )}

      <section className="review-section">
        <div className="section-heading" style={{ margin: '0 0 6px' }}>
          <h2>Preview as client</h2>
          <span className="count">{mode === 'on' ? `Exactly what ${clientName} will see` : mode === 'preview'
            ? `${clientName} is on Preview: they'll see the locked summary` : `SEO recommendations is Off for ${clientName}`}</span>
          <CopyAsText clientName={clientName} list={list} tasks={tasks} />
        </div>
        {mode === 'preview' && <LockedPreview clientId={clientId} clientName={clientName} />}
        <div className="gmb-preview-frame">
          <div className="gmb-preview-ribbon">{mode === 'on' ? 'Preview · not yet released' : 'The full checklist · not yet released'}</div>
          <SeoChecklistView list={list} tasks={visible} buckets={buckets} deferredTasks={deferred} you={false}
                            canTick={(t) => t.owner_group === 'client'} onToggle={null} preview renderTaskExtra={renderTaskExtra} />
        </div>
      </section>

      <TaskEditor tasks={tasks} onSaved={onChange} />

      <section className="review-section">
        <h2>Release to client</h2>
        <p className="hint" style={{ marginTop: 0 }}>
          {mode === 'on' && <>Makes the checklist visible to {clientName}, puts an item in their Inbox and sends them one "Your SEO checklist is ready" email.</>}
          {mode === 'preview' && <>{clientName} is on Preview: releasing shows them the locked summary (top 5 issues and the counts). No Inbox item and no email; tell them yourself.</>}
          {mode === 'off' && <>SEO recommendations is Off for {clientName}: releasing sends nothing and they see nothing until it's switched on.</>}
          {replacing ? ' It replaces the checklist they have now: items already done stay done, and items the new analysis no longer finds show as resolved.' : ''}
        </p>
        {err && <div className="banner banner-err">{err}</div>}
        <button type="button" className="btn btn-primary" onClick={() => setConfirm(true)} disabled={busy}>Release to client</button>
      </section>

      {confirm && (
        <div className="modal-backdrop" onClick={() => !busy && setConfirm(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="seo-release-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="seo-release-title">Release to {clientName}?</h2>
            <p>{visible.length} task{visible.length === 1 ? '' : 's'} go live{hiddenCount ? ` (${hiddenCount} stay hidden)` : ''}{deferred.length ? `; ${deferred.length} stay deferred` : ''}.
              {mode === 'on' ? ` ${clientName} gets an Inbox item and one email.` : ' No email is sent.'}</p>
            <div className="review-actions">
              <button type="button" className="btn" onClick={() => setConfirm(false)} disabled={busy}>Not yet</button>
              <button type="button" className="btn btn-primary" onClick={release} disabled={busy}>{busy ? 'Releasing…' : 'Yes, release'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ReleasedSummary({ list, tasks }) {
  const visible = tasks.filter((t) => !t.hidden);
  const done = visible.filter((t) => t.done_at || t.resolved_by_rerun_at).length;
  return (
    <div className="banner banner-ok gmb-released">
      <strong>Released {fmtDateTime(list.released_at)}.</strong>{' '}
      {done} of {visible.length} done · {list.client_tasks_open} open for the client
      {' · '}{list.client_first_viewed_at ? `Client first opened it ${fmtDateTime(list.client_first_viewed_at)}.` : 'The client has not opened it yet.'}
    </div>
  );
}

// ---------------------------------------------------------------------------

function TaskEditor({ tasks, onSaved, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);
  const ordered = useMemo(() => [...tasks].sort((a, b) => a.sort - b.sort), [tasks]);
  return (
    <section className="review-section">
      <div className="section-heading" style={{ margin: '0 0 6px' }}>
        <h2>Edit tasks</h2>
        {collapsed && <button type="button" className="link-btn" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'}</button>}
      </div>
      <p className="hint" style={{ marginTop: 0 }}>Change the wording, owner or priority, or hide a task from the client. Each row saves on its own.</p>
      {open && (
        <ul className="gmb-edit-list">
          {ordered.map((t) => <TaskEditRow key={t.task_id} task={t} onSaved={onSaved} />)}
        </ul>
      )}
    </section>
  );
}

function TaskEditRow({ task, onSaved }) {
  const fresh = (t) => ({ title: t.title || '', fix: t.fix || '', owner_group: t.owner_group, priority: t.priority, hidden: !!t.hidden });
  const [d, setD] = useState(() => fresh(task));
  const [busy, setBusy] = useState(false);
  const saved = useSaveStatus();   // Session 24: the shared Saved / Couldn't save line
  useEffect(() => { setD(fresh(task)); }, [task.task_id, task.edited_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = fresh(task);
  const changed = Object.fromEntries(Object.entries(d).filter(([k, v]) => v !== base[k]));
  const dirty = Object.keys(changed).length > 0;

  async function save() {
    setBusy(true); saved.start();
    const { error } = await supabase.rpc('portal_seo_edit_task', { p_task_id: task.task_id, p_fields: changed });
    setBusy(false);
    if (error) { saved.fail(error.message); return; }
    saved.ok();
    await onSaved();
  }

  return (
    <li className={`gmb-edit-row ${d.hidden ? 'is-hidden' : ''}`}>
      <div className="gmb-edit-meta">
        <span className="plan-card-type">#{task.n}</span>
        <span className="gmb-chip seo-cat">{CATEGORY_LABEL[task.category] || task.category}</span>
        <span className="muted">{task.issue_key}</span>
        {task.source === 'report' && <span className="gmb-chip gmb-needs-ok">From the report</span>}
        {task.done_at && <span className="gmb-chip gmb-done">Done</span>}
        {task.resolved_by_rerun_at && <span className="gmb-chip seo-resolved">Resolved</span>}
      </div>
      <p className="hint" style={{ margin: '4px 0 8px' }}>{task.found}</p>
      <div className="gmb-edit-fields">
        <label className="field">
          <span className="field-label">Title</span>
          <input type="text" value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} disabled={busy} />
        </label>
        <label className="field">
          <span className="field-label">What to do</span>
          <textarea rows={3} value={d.fix} onChange={(e) => setD({ ...d, fix: e.target.value })} disabled={busy} />
        </label>
        <div className="inline-fields">
          <label className="field">
            <span className="field-label">Owner</span>
            <select value={d.owner_group} onChange={(e) => setD({ ...d, owner_group: e.target.value })} disabled={busy}>
              <option value="client">{OWNER_LABEL.client} (client)</option>
              <option value="agency">{OWNER_LABEL.agency}</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Priority</span>
            <select value={d.priority} onChange={(e) => setD({ ...d, priority: e.target.value })} disabled={busy}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
          <div className="gmb-edit-toggles">
            <Toggle checked={d.hidden} onChange={(v) => setD({ ...d, hidden: v })} label="Hide from client" disabled={busy} />
          </div>
        </div>
        <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }}>
          <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</button>
          {dirty && <button type="button" className="link-btn" onClick={() => setD(base)} disabled={busy}>Undo</button>}
          <SaveStatus status={saved.status} />
        </div>
      </div>
    </li>
  );
}
